// src/pages/FundPlanner/components/FundStructurer/InvestmentSolution.jsx
import React, { useMemo, useEffect, useState } from "react";
import ExcelGrid from "components/ExcelGrid";
import { DropdownSelect, DateInput } from "components/InputStyled";
import { Copy } from "lucide-react";
import { supabase } from "services/supabaseClient";
import UsTreasuryResults from "./UsTreasuryResults";
import CustomCurveModal from "./CustomCurveModal";
import {
  HYPOTHETICAL_CURVE_UNIVERSES,
  normalizeHypotheticalPortfolioResult,
} from "pages/FundPlanner/lib/usTreasuryCalculator";

export default function InvestmentSolution({
  rows = [],
  solution = {},
  onSolve,
  isSolving = false,
  fundMode,
  setFundMode,
  securityType,
  setSecurityType,
  asOfDate,
  setAsOfDate,
  customCurveRows = [],
  setCustomCurveRows,
  userCurveDate = "",
  setUserCurveDate,
  userCurveDescription = "",
  setUserCurveDescription,
  isReadOnly = false,
}) {
  const [availableDates, setAvailableDates] = useState([]);
  const [showCustomCurve, setShowCustomCurve] = useState(false);

  /* ------------------------------------------------------------------
   * Render-level visibility (baseline)
   * ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
   * Load available market dates by universe (RPC)
   * ------------------------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;

    const loadMarketDates = async () => {
      if (!securityType) {
        setAvailableDates([]);
        return;
      }

      if (securityType === "custom") {
        setAvailableDates([]);
        return;
      }

      const prefix =
        securityType === "slgs"
          ? "slgs-time"
          : securityType === "ust"
          ? "ust"
          : "us-agcy";

      const { data, error } = await supabase.rpc("fp_market_dates", {
        security_prefix: prefix,
        max_rows: 500,
      });

      if (cancelled) return;

      if (error) {
        console.error("[IS] ❌ market dates RPC error:", error.message);
        setAvailableDates([]);
        return;
      }

      const uniqueDates = (data || [])
        .map((r) => r?.d)
        .filter(Boolean);

      setAvailableDates(uniqueDates);

      if (uniqueDates.length && !asOfDate) {
        setAsOfDate(uniqueDates[0]);
      }
    };

    loadMarketDates();

    return () => {
      cancelled = true;
    };
  }, [securityType, setAsOfDate]); // intentionally NOT depending on asOfDate

  /* ------------------------------------------------------------------
   * Formatters
   * ------------------------------------------------------------------ */
  const fmt2 = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const fmt3 = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })
      : "";

  /* ------------------------------------------------------------------
   * Totals / sorting
   * ------------------------------------------------------------------ */
  const totalCost = useMemo(
    () => rows.reduce((sum, r) => sum + (r.cost || 0), 0),
    [rows]
  );
  const hypotheticalConfig = HYPOTHETICAL_CURVE_UNIVERSES[securityType];
  const hypotheticalResult = hypotheticalConfig
    ? normalizeHypotheticalPortfolioResult(
        solution?.[hypotheticalConfig.resultKey],
        securityType
      )
    : null;
  const displayedTotalCost =
    hypotheticalConfig
      ? Number(hypotheticalResult?.summary?.portfolioCost)
      : totalCost;
  const hasResult =
    hypotheticalConfig
      ? Number.isFinite(displayedTotalCost) && Boolean(hypotheticalResult?.rows?.length)
      : rows.length > 0;

  const sortedRows = useMemo(() => {
    return [...rows].sort(
      (a, b) => new Date(a.maturityDate) - new Date(b.maturityDate)
    );
  }, [rows]);

  /* ------------------------------------------------------------------
   * Copy to clipboard
   * ------------------------------------------------------------------ */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayedTotalCost.toFixed(2));
    } catch (err) {
      console.error("[IS] clipboard copy failed:", err);
    }
  };

  /* ------------------------------------------------------------------
   * Grid columns
   * ------------------------------------------------------------------ */
  const columns = [
    { key: "type", label: "Type" },
    { key: "name", label: "Security" },
    {
      key: "maturityDate",
      label: "Maturity",
      date: true,
      className: "numeric",
    },
    ...(securityType === "slgs"
      ? [
          {
            key: "firstInterestPaymentDate",
            label: "First Int Dt",
            date: true,
            className: "numeric",
          },
        ]
      : []),
    {
      key: "ratePct",
      label: "Rate (%)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt3,
    },
    {
      key: "price",
      label: "Price (%)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: (v) =>
        Number.isFinite(Number(v)) ? Number(v).toFixed(3) : "100.000",
    },
    {
      key: "principal",
      label: "Principal ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "totalInterest",
      label: "Total Interest ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "cost",
      label: "Cost ($)",
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Controls + total */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4 justify-start">
          <DropdownSelect
            label="Investment Universe"
            value={securityType || ""}
            onChange={(next) => {
              if (isReadOnly) return;
              setSecurityType(next || null);
              setAsOfDate("");
            }}
            options={[
              { value: "slgs", label: "SLGS" },
              { value: "ust", label: "US Treasury" },
              { value: "agency", label: "US Agencies" },
              { value: "custom", label: "User-Defined Curve" },
            ]}
            className="w-56"
            disabled={isReadOnly}
          />

          {securityType !== "custom" && <DateInput
            label="Market as of"
            value={asOfDate || ""}
            onChange={(v) => {
              if (isReadOnly) return;
              setAsOfDate(v || "");
            }}
            allowedDates={availableDates}
            disabled={isReadOnly || !securityType || availableDates.length === 0}
            className="w-48"
          />}

          {securityType === "custom" && (
            <button
              type="button"
              onClick={() => setShowCustomCurve(true)}
              disabled={isReadOnly}
              className="btn btn-secondary mt-1"
            >
              Edit Curve ({customCurveRows.length})
            </button>
          )}

          {!hypotheticalConfig && (
            <DropdownSelect
              label="Funding Mode"
              value={fundMode}
              onChange={setFundMode}
              options={[
                { value: "gross", label: "Gross Fund (Yield Max)" },
                { value: "net", label: "Net Fund (Cost Min)" },
              ]}
              className="w-56"
              disabled={isReadOnly}
            />
          )}

          <button
            type="button"
            onClick={onSolve}
            disabled={isSolving || isReadOnly}
            className="btn btn-primary mt-1 flex items-center gap-2"
          >
            {isSolving && (
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-white/30 border-t-white animate-spin"
              />
            )}
            {isSolving
              ? hypotheticalConfig ? "Calculating..." : "Solving..."
              : hypotheticalConfig ? "Calculate" : "Solve"}
          </button>
        </div>

        {hasResult && (
          <div className="flex items-center gap-2 mt-2 ml-auto">
            <span className="text-sm text-muted">Total Portfolio Cost:</span>
            <span className="font-semibold text-app text-md">
              ${fmt2(displayedTotalCost)}
            </span>
            <button
              onClick={handleCopy}
              title="Copy total cost"
              className="p-1 hover:text-brand transition-colors"
            >
              <Copy size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      {hypotheticalConfig ? (
        <UsTreasuryResults
          result={hypotheticalResult}
          universeLabel={hypotheticalConfig.universeLabel}
        />
      ) : rows.length > 0 ? (
        <ExcelGrid columns={columns} rows={sortedRows} />
      ) : (
        <p className="text-muted italic">No investment solution generated yet.</p>
      )}

      {showCustomCurve && (
        <CustomCurveModal
          rows={customCurveRows}
          curveDate={userCurveDate}
          description={userCurveDescription}
          onSave={({ date, description: nextDescription, rows: nextRows }) => {
            setUserCurveDate(date);
            setUserCurveDescription(nextDescription);
            setCustomCurveRows(nextRows);
          }}
          onClose={() => setShowCustomCurve(false)}
        />
      )}
    </div>
  );
}
